import React, { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { APP_NAME } from '../../constants/Brand';
import { Themes } from '../../constants/Enums';
import HeaderContainer from '../../containers/HeaderContainer';
import StaticContainer from '../../containers/StaticContainer';
import { SidebarProvider } from '../../contexts/SidebarContext';
import syncAppTheme from '../../design-system/utils/syncAppTheme';
import { beautifyLink } from '../../utils/url';
import Background from '../Background';
import { Loader, LoaderSize, Button, ButtonVariant, Icon, IconType, IconSize } from '../Utils';

import * as s from './Core.module.scss';

const Core = React.memo(({ isInitializing, isSocketDisconnected, currentProject, currentBoard, currentCard, theme, themeShape, themeCustomColors }) => {
  const [t] = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showDisconnected, setShowDisconnected] = useState(false);

  useEffect(() => {
    let title = APP_NAME;
    if (currentProject) {
      title = `${currentProject.name} | ${APP_NAME}`;
      if (currentBoard) {
        title = `${currentBoard.name} · ${currentProject.name} | ${APP_NAME}`;
        if (currentCard) {
          const parts = currentCard.name.split(/(\s+)/);
          const cardName = parts.map((part) => beautifyLink(part)).join('');
          title = `${cardName} - ${currentBoard.name} · ${currentProject.name} | ${APP_NAME}`;
        }
      }
    }
    document.title = title;
  }, [currentBoard, currentCard, currentProject]);

  useEffect(() => {
    const body = document.getElementById('app');
    body?.setAttribute('data-theme', theme);
    body?.removeAttribute('style');
    syncAppTheme(theme);

    if (theme === Themes.LIGHT) {
      document.documentElement.setAttribute('data-color-mode', 'light');
    } else {
      document.documentElement.setAttribute('data-color-mode', 'dark');
    }

    if (theme === Themes.CUSTOM) {
      Object.entries(themeCustomColors).forEach(([varName, value]) => {
        body?.style.setProperty(varName, value);
      });
    }
  }, [theme, themeCustomColors]);

  useEffect(() => {
    const body = document.getElementById('app');
    if (body) {
      body.setAttribute('data-theme-shape', themeShape);
    }
  }, [themeShape]);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    let disconnectedTimeout;
    if (isSocketDisconnected) {
      disconnectedTimeout = setTimeout(() => setShowDisconnected(true), 20);
    } else {
      setShowDisconnected(false);
    }
    return () => clearTimeout(disconnectedTimeout);
  }, [isSocketDisconnected]);

  return (
    <>
      {isInitializing ? (
        <div className={s.bootLoader}>
          <Loader size={LoaderSize.Massive} />
        </div>
      ) : (
        <>
          {currentProject && currentProject.background && (
            <Background type={currentProject.background.type} name={currentProject.background.name} imageUrl={currentProject.backgroundImage && currentProject.backgroundImage.url} />
          )}
          <SidebarProvider>
            <HeaderContainer />
            <StaticContainer />
          </SidebarProvider>
        </>
      )}
      {showDisconnected && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className={clsx(s.message, isCollapsed && s.messageCollapsed)} onClick={handleToggleCollapse}>
          <div className={s.messageHeader}>
            <Icon className={clsx(s.messageIcon, isCollapsed && s.messageIconCollapsed)} type={IconType.NoConnection} size={IconSize.Size20} />
            <div className={clsx(s.messageTitle, isCollapsed && s.collapsed)}>{t('common.noConnection')}</div>
            <Button variant={ButtonVariant.Icon} title={t('common.close')} className={clsx(s.messageCloseButton, isCollapsed && s.collapsed)} onClick={handleToggleCollapse}>
              <Icon className={s.messageCloseIcon} type={IconType.Close} size={IconSize.Size16} />
            </Button>
          </div>
          <div className={clsx(s.messageContent, isCollapsed && s.collapsed)}>{t('common.allChangesWillBeAutomaticallySaved')}</div>
        </div>
      )}
    </>
  );
});

Core.propTypes = {
  isInitializing: PropTypes.bool.isRequired,
  isSocketDisconnected: PropTypes.bool.isRequired,
  currentProject: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  currentBoard: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  currentCard: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  theme: PropTypes.string,
  themeShape: PropTypes.string,
  themeCustomColors: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

Core.defaultProps = {
  currentProject: undefined,
  currentBoard: undefined,
  currentCard: undefined,
  theme: 'default',
  themeShape: 'default',
  themeCustomColors: {},
};

export default Core;
